<main class="home">
  <div class="hero-photo" aria-labelledby="home-hero-heading">
    <picture class="hero-photo__picture">
      <source
        media="(max-width: 767px)"
        [srcset]="heroImageWebp800"
        type="image/webp">
      <source
        [srcset]="heroImageWebp1600"
        type="image/webp">
      <img
        class="hero-photo__image"
        [src]="heroImageWebp1600"
        [srcset]="heroImageSrcset"
        sizes="100vw"
        alt=""
        aria-hidden="true"
        width="1600"
        height="1065"
        fetchpriority="high"
        loading="eager"
        decoding="async">
    </picture>
    <div class="hero-photo__overlay" aria-hidden="true"></div>
    <div class="hero-photo__content">
      <h1 id="home-hero-heading" class="bold">Find your home in South Texas</h1>
      <div class="search-bar">
        <app-google-place-autocomplete
          [lazyLoad]="true"
          inputId="home-listing-search"
          inputName="home-listing-search"
          placeholder="City, address, or ZIP"
          [options]="placeOptions"
          #placesRef="googlePlaceAutocomplete"
          (fallbackValueChange)="onSearchInput($event)"
          (onAddressChange)="handleAddressChange($event)"
          (keyupEnter)="submitAddress()">
        </app-google-place-autocomplete>
        <button type="button" mat-flat-button color="accent" (click)="submitAddress()">Search</button>
      </div>

      <ul class="home__type-chips" aria-label="Browse by property type">
        @for (link of propertyTypeLinks; track link.type) {
          <li>
            <a
              class="home__type-chip"
              [routerLink]="['/featured-properties']"
              [queryParams]="{ type: link.type }">
              {{ link.label }}
            </a>
          </li>
        }
      </ul>
    </div>
  </div>

  <section class="home__intro" aria-label="About Gabali Realty">
    <p class="home__lede">
      Gabali Realty is a boutique brokerage built for South Texas — helping buyers find the right property
      and guiding sellers from pricing through closing with a team that knows this market.
    </p>
    <ul class="home__highlights">
      @for (item of highlights; track item) {
        <li class="home__highlight">{{ item }}</li>
      }
    </ul>
  </section>

  <section class="home__featured" *ngIf="featuredListings.length > 0" aria-labelledby="featured-heading">
    <header class="home__section-header">
      <h2 id="featured-heading" class="home__section-title">Featured listings</h2>
      <p class="home__section-intro">
        A sample of active homes, land, and commercial properties on the market now.
      </p>
    </header>

    <div class="home__featured-grid">
      @for (listing of featuredListings; track listing.ListingID; let index = $index) {
        <app-residential-card
          class="home__featured-card"
          [property]="listing"
          [propertyType]="propertyTypeFor(listing)"
          [photoLoading]="index < 2 ? 'eager' : 'lazy'">
        </app-residential-card>
      }
    </div>

    <div class="home__featured-footer">
      <a class="home__featured-link" routerLink="/featured-properties">
        View all listings
        <span aria-hidden="true">→</span>
      </a>
    </div>
  </section>

  <section class="home__featured home__featured--loading" *ngIf="listingsLoading && featuredListings.length === 0" aria-busy="true">
    <header class="home__section-header">
      <h2 class="home__section-title">Featured listings</h2>
      <p class="home__section-intro">Loading active properties…</p>
    </header>
    <div class="home__featured-skeleton" aria-hidden="true">
      @for (slot of [1, 2, 3]; track slot) {
        <div class="home__featured-skeleton-card"></div>
      }
    </div>
  </section>

  <section class="home__reviews" *ngIf="reviewsLoading || zillowReviews.length > 0" aria-labelledby="reviews-heading">
    <header class="home__section-header">
      <h2 id="reviews-heading" class="home__section-title">Trusted by buyers &amp; sellers</h2>
      <p class="home__section-intro" *ngIf="!reviewsLoading && zillowReviews.length > 0">
        {{ avgStarRating }} stars across {{ totalReviewCount }} reviews from clients who worked with Gabali Realty.
      </p>
      <p class="home__section-intro" *ngIf="reviewsLoading">Loading client reviews…</p>
    </header>

    <div class="home__reviews-skeleton" *ngIf="reviewsLoading" aria-hidden="true"></div>

    <div class="home__reviews-carousel" *ngIf="!reviewsLoading && zillowReviews.length > 0">
      <button
        type="button"
        class="home__review-nav home__review-nav--prev"
        aria-label="Previous review"
        [disabled]="activeReviewIndex === 0"
        (click)="showPreviousReview()">
        <span aria-hidden="true">‹</span>
      </button>

      <div
        class="review-viewport"
        [class.review-viewport--dragging]="isReviewDragging"
        [style.--slide-count]="zillowReviews.length"
        (touchstart)="onReviewDragStart($event)"
        (touchmove)="onReviewDragMove($event)"
        (touchend)="onReviewDragEnd()">
        <div
          class="review-track"
          [class.review-track--dragging]="isReviewDragging"
          [style.transform]="getReviewTrackTransform()">
          @for (review of zillowReviews; track $index) {
            <article class="review-card">
              <div class="review-card-source">
                <span class="source-label">Provided by</span>
                <svg class="zillow-logo" viewBox="0 0 1911 400" focusable="false" aria-label="Zillow">
                  <path d="M1460.4 148.6h77.8l22.5 73.7c7.5 24.4 20.1 70.2 20.1 70.2a.6.6 0 0 0 1.1 0s13-46.7 20.5-70.2l23.6-73.7h57.4l23.3 73.7c7.8 25.2 20 70.2 20 70.2a.6.6 0 0 0 1.1 0s12.7-45.6 20-70.2l22.6-73.7h72.3L1764.5 392h-71.1l-19.4-61c-7.5-24-20.6-70.6-20.6-70.6a.6.6 0 0 0-1.1 0s-13.1 45.7-21 70.2l-19.7 61.6h-72.4zM936.8 19.9h78.7v372.2h-78.7zM1073.8 19.9h78.7v372.2h-78.7zM1380.7 271.2c0-36.3-25.9-61-56.5-61-31.3 0-57.2 24.7-57.2 61 0 36.5 25.9 60.9 57.2 60.9 30.6 0 56.5-24.4 56.5-61m-189.2 0c0-74 59.7-128.7 132.7-128.7 72.3 0 132 54.8 132 128.8 0 74.2-59.7 129-132 129-73 0-132.7-54.8-132.7-129M481.5 54.4V126h139c.7 0 .9.5.6 1L481.4 364.5c-3.4 6.2-4.2 7.5-4.2 13.6v14h271v-71.6H597.7a.6.6 0 0 1-.5-1L738.5 79c1.1-2.3 2.3-4 2.3-9.6v-15zM798.4 148.6H877V392h-78.7zm39.3-122c24.6 0 44.8 19.8 44.8 44.2S862.3 115 837.7 115 793 95.2 793 70.8s20.3-44.2 44.8-44.2"></path>
                  <path d="M242.2 106.3c1.8-.4 2.6.2 3.7 1.4 6.1 7 26 31.1 31.4 37.7a2.1 2.1 0 0 1-.5 3.2 714.8 714.8 0 0 0-109.3 107c-.5.6-.1.7.2.5 43.3-18.6 144.7-48.2 190.5-56.6v-57.9L179.2.2.2 141.6V205c55.5-33 183.9-84.1 242-98.6z"></path>
                  <path d="M96.6 349.5a3 3 0 0 1-3.8-.6l-33.5-39.8c-.9-1-1-1.6.2-3.4C85.3 268 138 209 171.5 184.1c.7-.5.5-1-.2-.7C136.3 195 37 237.6 0 258.4v135.2h358.2V263.5c-49 8.3-195.1 51.8-261.6 86z"></path>
                </svg>
                <a class="source-link" [href]="profileURL">See more reviews</a>
              </div>
              <div class="review-content">
                <p class="review-meta">{{ review.reviewDate }} · {{ review.reviewer }}</p>
                <p class="review-summary">{{ review.reviewSummary }}</p>
                <p class="review-description">{{ review.description }}</p>
                <a class="review-link" [href]="review.reviewURL">Read on Zillow</a>
              </div>
            </article>
          }
        </div>
      </div>

      <button
        type="button"
        class="home__review-nav home__review-nav--next"
        aria-label="Next review"
        [disabled]="activeReviewIndex >= zillowReviews.length - 1"
        (click)="showNextReview()">
        <span aria-hidden="true">›</span>
      </button>

      <div class="review-indicators">
        @for (review of zillowReviews; track $index; let i = $index) {
          <button
            type="button"
            class="review-indicator"
            [class.active]="i === activeReviewIndex"
            [attr.aria-label]="'Show review ' + (i + 1)"
            (click)="setActiveReview(i)">
          </button>
        }
      </div>
    </div>
  </section>

  <section class="home__resources" aria-labelledby="resources-heading">
    <header class="home__section-header">
      <h2 id="resources-heading" class="home__section-title">Resources for your next move</h2>
      <p class="home__section-intro">
        Guides, estimates, and direct contact — pick the path that fits where you are in the process.
      </p>
    </header>
    <div class="home__resource-grid">
      @for (resource of resources; track resource.link) {
        <article class="home__resource-card">
          <h3 class="home__resource-title">{{ resource.title }}</h3>
          <p class="home__resource-copy">{{ resource.description }}</p>
          <a class="home__resource-link" [routerLink]="resource.link">
            {{ resource.linkLabel }}
            <span aria-hidden="true">→</span>
          </a>
        </article>
      }
    </div>
  </section>
</main>
